/**
 * Live progress events for one runbook run.
 *
 * @module runbook-run-events
 * @description The SSE vocabulary emitted on `GET /jobs/{runId}/stream` — keyed
 * on the RUN id, not the job id. `POST /org/runbooks/:id/run` creates the run
 * row and separately enqueues a job whose id is never returned to the client,
 * so the run id is the only identifier a subscriber can hold. Same reasoning as
 * `ExternalRunEvent`, for a different reason: not because the run fans out into
 * many jobs, but because the client is handed the run and never the job.
 *
 * Shared rather than declared twice so the emitter
 * (`apps/api/src/runbooks/events.ts`) and the subscriber (`apps/web`'s
 * `useRunbookRunStream`) cannot drift — a renamed field would otherwise just
 * stop rendering, silently.
 *
 * Why a stream exists at all: a deploy runs for minutes to hours. Without live
 * output the operator sees a spinner and nothing else until it ends, which is
 * the difference between a deploy button someone trusts and one they don't.
 */

/** Terminal and non-terminal states a run reports on the stream. */
export type RunbookRunStreamStatus =
  | 'queued'
  | 'running'
  | 'succeeded'
  | 'failed'
  | 'cancelled'
  | 'timed_out';

export type RunbookRunEvent =
  | {
      type: 'runbook-run.status';
      data: {
        runId: string;
        status: RunbookRunStreamStatus;
        /** Present only on a terminal status, and null for a signal kill. */
        exitCode?: number | null;
        /**
         * OUR failure, never the script's. A script that exited non-zero is
         * explained by its own output and exit code; this carries the reason a
         * run never got far enough to produce either.
         */
        error?: string | null;
        emittedAt: string;
      };
    }
  | {
      type: 'runbook-run.log';
      data: {
        runId: string;
        /**
         * Monotonic index of this delta within the run, from 0.
         *
         * Load-bearing for correctness, not diagnostics. The stream's replay
         * cursor drops cached events with `emittedAt < since`, so the event AT
         * the cursor is re-sent on every reconnect — harmless for status, which
         * folds idempotently, and wrong for a delta, which APPENDS. A
         * subscriber must ignore any `seq` it has already applied.
         */
        seq: number;
        /**
         * Bytes appended since the previous event, NOT the whole log. The
         * subscriber concatenates. Empty deltas are never emitted, so an event
         * arriving always means new output.
         *
         * `stdout` and `stderr` stay separate because the client renders them
         * differently. Note this makes the live view chronological while the
         * persisted log (`run.job.ts` writes `stdout` then `stderr`) is
         * grouped — the live ordering is the truer one, and reconciling them
         * would mean interleaving at capture time, which the guest's two
         * independent offset cursors cannot do.
         */
        stdout: string;
        stderr: string;
        emittedAt: string;
      };
    };

/**
 * Every event name, for the subscriber to register listeners against.
 * `satisfies` keeps it exhaustive: adding a variant above without adding it
 * here fails the build rather than silently going unlistened-for.
 */
export const RUNBOOK_RUN_EVENT_TYPES = [
  'runbook-run.status',
  'runbook-run.log',
] as const satisfies readonly RunbookRunEvent['type'][];
